import { useMemo, useState } from 'react';

import moment from 'moment';
import { TOrder } from 'types/Orders/order';

import { TDateRangeHeaderProps } from '@molecules/dateRangeCalendar/dateRangeCalendarModal';
import { TRangeEdge } from '@molecules/dateRangeCalendar/hooks/useDateRangeSelection';

import { OrderDateChangeModal } from '../../../modals/orderDateChangeModal/orderDateChangeModal';
import { OrderDateRange } from './orderDateRange';

import style from '../orderCreateHeader.module.scss';

/** Поле заказа, которое правит старая модалка, — по паре дат и краю диапазона */
const FIELD_BY_EDGE = {
    planned: { start: 'start_planned_at', end: 'finish_planned_at' },
    actual: { start: 'started_at', end: 'finished_at' },
} as const;

type TProps = TDateRangeHeaderProps & {
    kind: keyof typeof FIELD_BY_EDGE;
};

/**
 * Шапка «Запись с … по …» в календаре дат — в заказе и в мастере создания.
 * Тап по дате открывает прежнюю модалку с произвольным временем — для минут
 * не из шага календаря и дат вне сетки.
 */
export const OrderDatesHeader = ({ kind, start, end, setEdge }: TProps) => {
    const [editedEdge, setEditedEdge] = useState<TRangeEdge | null>(null);
    const field = FIELD_BY_EDGE[kind][editedEdge ?? 'start'];

    // Старая модалка (её не меняем) читает из заказа только поле `type` — даём ей
    // черновик с одним этим полем: в мастере создания заказа ещё нет
    const draftOrder = useMemo<Partial<TOrder>>(
        () => ({ [field]: editedEdge === 'end' ? end?.format() : start?.format() }),
        [field, editedEdge, start, end]
    );
    // Без окончания — через час после начала; мемоизировано: модалка следит за ссылкой
    const defaultEnd = useMemo(() => (start ? start.clone().add(1, 'hour').toDate() : undefined), [start]);

    return (
        <>
            <div className="breadcrumb-header">
                <div className={`${style['breadcrumb-wrapper']} ${style['dates-modal-header']}`}>
                    <OrderDateRange
                        startDate={start?.format() ?? null}
                        endDate={end?.format() ?? null}
                        onStartClick={() => setEditedEdge('start')}
                        onEndClick={() => setEditedEdge('end')}
                    />
                </div>
            </div>

            <OrderDateChangeModal
                open={editedEdge !== null}
                onCancel={() => setEditedEdge(null)}
                // Модалка отдаёт строку «YYYY-MM-DD HH:mm:ss», хоть и объявляет `Date`
                onOk={(value) => {
                    if (editedEdge) setEdge(editedEdge, moment(value));
                    setEditedEdge(null);
                }}
                orderData={draftOrder as TOrder}
                type={field}
                defaultDate={editedEdge === 'end' ? defaultEnd : undefined}
            />
        </>
    );
};
